import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { ValidatorFn } from '@angular/forms'
import { BehaviorSubject, combineLatest, throwError } from 'rxjs'
import { finalize } from 'rxjs/operators'

import { environment } from '../../../../environments/environment'
import { AuthService } from '../../services/auth.service'
import { LoginPromptService } from '../../services/login-prompt.service'
import { Market } from '../../types/market.type'
import { ApiResponse } from '../../types/response.type'
import { PlaceBetRequest } from './types/place-bet.type'
import { SelectedRunner } from './types/selected-runner.type'

const API_URL = environment.marketUrl
export type PlaceBetModalToggleResult = 'opened' | 'closed' | 'blocked'

@Injectable({
	providedIn: 'root',
})
export class PlaceBetModalService {
	readonly #market$ = new BehaviorSubject<Market | null>(null)

	readonly #selectedRunner$ = new BehaviorSubject<SelectedRunner | null>(null)

	readonly #additionalStakeValidators$ = new BehaviorSubject<ValidatorFn[]>([])

	readonly #isModalOpen$ = new BehaviorSubject<boolean>(false)

	readonly #currentPlacement$ = new BehaviorSubject<SelectedRunner | null>(null)

	private readonly PLACING_BET_KEY = 'placing_bet_flag'

	constructor(
		private readonly httpClient: HttpClient,
		private readonly authService: AuthService,
		private readonly loginPromptService: LoginPromptService,
	) {
		combineLatest([this.#market$, this.#selectedRunner$]).subscribe({
			next: ([market, selectedRunner]) => {
				this.#isModalOpen$.next(market !== null && selectedRunner !== null)
			},
		})
	}

	get market$() {
		return this.#market$
	}

	get selectedRunner$() {
		return this.#selectedRunner$
	}

	get additionalStakeValidators$() {
		return this.#additionalStakeValidators$
	}

	get currentPlacement$() {
		return this.#currentPlacement$
	}

	set setAdditionalStakeValidators(validators: ValidatorFn[]) {
		this.#additionalStakeValidators$.next(validators)
	}

	showPlaceBetModal(market: Market, selectedRunner: SelectedRunner, additionalStakeValidators: ValidatorFn[] = []) {
		this.openPlaceBetModal(market, selectedRunner, additionalStakeValidators)
	}

	togglePlaceBetModal(market: Market, selectedRunner: SelectedRunner, additionalStakeValidators: ValidatorFn[] = []): PlaceBetModalToggleResult {
		if (this.isSameSelectedRunner(selectedRunner)) {
			this.dismissPlaceBetModal()
			return 'closed'
		}

		return this.openPlaceBetModal(market, selectedRunner, additionalStakeValidators) ? 'opened' : 'blocked'
	}

	dismissPlaceBetModal() {
		this.#market$.next(null)
		this.#selectedRunner$.next(null)
		this.#additionalStakeValidators$.next([])
	}

	placeBet(data: PlaceBetRequest) {
		if (this.shouldBlockBetPlacement()) {
			this.showLoginRequiredMessage()
			return throwError(() => new Error('Please Login'))
		}

		// Check cross-tab protection using localStorage
		const isPlacingInAnotherTab = localStorage.getItem(this.PLACING_BET_KEY) === 'true'

		// Global protection against concurrent bet placement (same tab + cross-tab)
		if (isPlacingInAnotherTab) {
			return throwError(() => new Error('Another bet is already processing'))
		}

		const operationId = this.createOperationId()
		localStorage.setItem(this.PLACING_BET_KEY, 'true')

		return this.httpClient.post<ApiResponse<{ ID: number }[]>>(`${API_URL}/bf/place-bet`, data, { headers: { 'X-Op-Id': operationId } }).pipe(
			finalize(() => {
				setTimeout(() => {
					localStorage.removeItem(this.PLACING_BET_KEY)
				}, 5000)
			}),
		)
	}

	private createOperationId(): string {
		if (globalThis.crypto?.randomUUID) {
			return globalThis.crypto.randomUUID()
		}

		const bytes = new Uint8Array(16)
		if (!globalThis.crypto?.getRandomValues) {
			throw new Error('Secure random number generation is unavailable.')
		}

		globalThis.crypto.getRandomValues(bytes)
		bytes[6] = (bytes[6] & 0x0f) | 0x40
		bytes[8] = (bytes[8] & 0x3f) | 0x80

		const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))
		return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`
	}

	private shouldBlockBetPlacement(): boolean {
		return !this.authService.getAccessToken() || this.authService.isDemoUser()
	}

	private showLoginRequiredMessage() {
		this.loginPromptService.showLoginRequired()
	}

	private openPlaceBetModal(market: Market, selectedRunner: SelectedRunner, additionalStakeValidators: ValidatorFn[] = []): boolean {
		if (this.shouldBlockBetPlacement()) {
			this.showLoginRequiredMessage()
			return false
		}

		this.#market$.next(market)
		this.#selectedRunner$.next(selectedRunner)
		this.#additionalStakeValidators$.next(additionalStakeValidators)
		return true
	}

	private isSameSelectedRunner(selectedRunner: SelectedRunner): boolean {
		const currentSelectedRunner = this.#selectedRunner$.getValue()

		return Boolean(
			currentSelectedRunner &&
				currentSelectedRunner.marketId === selectedRunner.marketId &&
				currentSelectedRunner.marketGroupId === selectedRunner.marketGroupId &&
				currentSelectedRunner.selectionId === selectedRunner.selectionId &&
				currentSelectedRunner.side === selectedRunner.side &&
				(currentSelectedRunner.priceLevel ?? '') === (selectedRunner.priceLevel ?? '') &&
				currentSelectedRunner.handicap === selectedRunner.handicap &&
				currentSelectedRunner.provider === selectedRunner.provider &&
				currentSelectedRunner.providerId === selectedRunner.providerId &&
				currentSelectedRunner.serviceId === selectedRunner.serviceId,
		)
	}
}
